import type { PageContent } from '../core/types/pages'

/**
 * Alle paginateksten, zoals ze nu lokaal staan: exact de vorm die de backend
 * straks op GET /pages levert. Tokens als {firstName} vult de site zelf in.
 *
 * Wordt in place bijgewerkt door `applyPageContent` (data/site.ts),
 * dus altijd lezen via `pages.home.hero` en nooit een kopie bewaren.
 */
export const pages: PageContent = {
  home: {
    hero: {
      eyebrow: 'Huidspecialist & vegan skincare in {city}',
      lines: [{ text: 'Huidverbetering die' }, { text: 'thuis doorgaat.', accent: true }],
      body: 'Ik ben {firstName}, {years} jaar huidspecialist. In de salon behandel ik je huid, thuis werk je verder met precies de producten die ik zelf gebruik.',
      cta: 'Vind wat jouw huid nodig heeft',
      salonDoor: {
        title: 'De salon',
        body: 'Behandelingen en huidanalyse in {city}, altijd door {firstName} zelf.',
        cta: 'Bekijk behandelingen',
      },
      shopDoor: {
        title: 'De webshop',
        body: '{count} producten die hun werk in de salon bewezen. {freeShipping}.',
        cta: 'Naar de webshop',
      },
      method: { label: 'Werkwijze', value: 'Eén op één' },
      // Leeg = het portret van Sarah uit Instellingen › Salon & contact (docs/BEELDEN.md, H1).
      media: { image: '', alt: '' },
    },
    manifest: {
      eyebrow: 'Waar ik voor sta',
      statement:
        'Ik kies alleen wat werkt, in de juiste volgorde, en zeg eerlijk wat je kunt overslaan. Een behandeling zet de toon; wat je daarna thuis doet, maakt het verschil.',
      accent: 'Daarom werk ik met allebei: de salon voor de start, de juiste producten voor elke dag daarna.',
      cta: 'Waarom ik zo werk',
      homeShare: 70,
      salonLabel: 'van je resultaat in de salon',
      homeLabel: 'thuis, met de juiste producten',
    },
    concerns: {
      heading: { text: 'Waar loopt jouw huid', accent: 'tegenaan?' },
      body: 'Kies wat het meest herkenbaar is. Je ziet direct welke producten ik meegeef, en welke behandeling erbij hoort.',
    },
    bestsellers: {
      heading: { text: 'Wat ik het vaakst', accent: 'meegeef.' },
      body: 'Getest op de behandelstoel, daarna pas in de shop. Dit zijn de producten die het vaakst met cliënten mee naar huis gaan.',
      cta: 'Alle {count} producten',
    },
    story: {
      eyebrow: 'De salon in {city}',
      heading: { text: 'Eerst je huid,', accent: 'dan pas producten.' },
      body: 'Eén behandelkamer, één cliënt tegelijk, altijd door mij. In drie stappen naar een routine die bij je past, in de salon of online.',
      steps: [
        // Beeld per stap; leeg = het salonbeeld hieronder, het portret met producten, het portret buiten (docs/BEELDEN.md, H2).
        {
          title: 'Kijken',
          body: 'Ik kijk naar je huid, je routine en je leven: hormonen, slaap, stress. Pas daarna praten we over producten.',
          media: { image: '', alt: '' },
        },
        {
          title: 'Kiezen',
          body: 'Alleen wat je huid nú nodig heeft. Vaak zijn dat minder producten, maar wel de juiste, in de juiste concentratie.',
          media: { image: '', alt: '' },
        },
        {
          title: 'Opbouwen',
          body: 'Stap voor stap introduceren, met een huidplan op papier. Na drie weken vraag ik hoe het gaat en sturen we bij.',
          media: { image: '', alt: '' },
        },
      ],
      cta: 'Plan een behandeling',
      secondaryCta: 'Alle behandelingen',
      consultLine: 'Liever eerst advies op afstand? Plan een {consultName} van {consultMinutes} minuten.',
      media: {
        // Tijdelijk sfeerbeeld, niet de salon zelf: vervangen vóór livegang (docs/BEELDEN.md).
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/e7b2132e-c4c7-4f2e-9292-deb45e0f57e7.jpg',
        alt: 'Twee handen die een gezichtsmassage geven',
        focus: { x: 55, y: 50 },
      },
    },
    reviews: {
      heading: { text: 'Wat cliënten zeggen' },
      body: 'Van cliënten die in de salon begonnen en thuis doorgingen. Geen gesponsorde reviews.',
    },
    protocols: {
      heading: { text: 'Het complete plan,', accent: 'kant-en-klaar' },
      body: 'Complete routines voor drie tot vijf maanden: de juiste producten, in de juiste volgorde, met een huidplan op papier. Zoals ik ze in de salon opstel.',
      note: 'Geldt voor elke bestelling, ook voor losse producten.',
      cta: 'Alle protocollen bekijken',
    },
    journal: {
      heading: { text: 'Tips en bevindingen uit de salon' },
      cta: 'Alles lezen & luisteren',
      askLine: 'Staat je vraag er niet tussen? App {firstName}: {phone}',
    },
    visit: {
      heading: { text: 'Kom langs in', accent: '{city}' },
      body: 'Eén rustige behandelkamer, alleen op afspraak. Kom voor een behandeling of een huidanalyse; je producten bestel je daarna gewoon online bij.',
      cta: 'Plan een afspraak',
      secondaryCta: 'Plan je route',
      media: {
        // Tijdelijk sfeerbeeld, niet de salon zelf: vervangen vóór livegang (docs/BEELDEN.md).
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/7166f87a-c402-483f-8556-b060455341f8.jpg',
        alt: 'Sfeerbeeld: een behandelbed met linnen in daglicht',
      },
    },
  },

  shop: {
    hero: {
      heading: { text: 'Vegan, maar dan', accent: 'wél werkzaam.' },
      body: '{count} van {brands}. Allemaal door {firstName} zelf gebruikt in de salon.',
      secondaryCta: 'Twijfel je? Huidadvies in één minuut',
    },
    ingredients: {
      heading: { text: 'Wat er', accent: 'écht in zit.' },
      body: 'Geen geheime formules. Dit zijn de stoffen die het werk doen, wat ze doen, en in welke producten je ze vindt.',
      items: [
        {
          name: 'Vitamine C',
          match: ['ascorbate', 'vitamine c', 'stay-c'],
          effect: 'Remt pigment, geeft glow en vangt vrije radicalen weg. Overdag, onder je SPF.',
        },
        {
          name: 'Vitamine A',
          match: ['retin'],
          effect: 'Versnelt de celvernieuwing: fijne lijntjes, pigment en onzuiverheden. Rustig opbouwen, alleen ’s avonds.',
        },
        {
          name: 'Peptiden',
          match: ['peptide', 'miniprotein', 'matrixyl'],
          effect: 'Signaalstofjes die de huid aanzetten tot eigen collageen. Mild, ook voor een gevoelige huid.',
        },
        {
          name: 'Niacinamide',
          match: ['niacinamide'],
          effect: 'Kalmeert roodheid, verfijnt poriën en versterkt de huidbarrière.',
        },
        {
          name: 'Exfoliërende zuren',
          match: ['salicyl', 'amandelzuur', 'melkzuur', 'glycolzuur'],
          effect: 'Lossen dode huidcellen op. Salicylzuur werkt ook ín de porie, amandelzuur is het zachtst.',
        },
        {
          name: 'Hyaluronzuur',
          match: ['hyaluron'],
          effect: 'Houdt vocht vast in de bovenste huidlagen. Geen wondermiddel, wel een goede basis.',
        },
      ],
    },
    routines: {
      heading: { text: 'Liever niet zelf puzzelen?' },
      body: 'Neem het protocol dat ik zou opstellen: dezelfde prijzen, maar in de juiste volgorde en met een huidplan op papier.',
      cta: 'Alle protocollen',
    },
  },

  routines: {
    hero: {
      eyebrow: 'Thuisprotocollen van {firstName}',
      heading: { text: 'Producten die', accent: 'samenwerken.' },
      body: 'De meeste huidproblemen komen niet door te weinig producten, maar door producten die elkaar tegenwerken. Deze protocollen stel ik ook in de salon op: met huidplan, opbouwschema en begeleiding.',
      cta: 'Welk protocol past bij mij?',
      secondaryCta: 'Losse producten',
      // Leeg = het sfeerbeeld van het complete protocol (docs/BEELDEN.md, R1).
      media: { image: '', alt: '' },
    },
    gifts: {
      heading: 'Wat er kosteloos bij komt',
      note: 'Twijfel je? App {firstName} vóór je bestelt. Ook als je niets koopt.',
    },
    steps: [
      {
        title: 'Vertel wat je huid doet',
        body: 'Vier vragen over je klacht, je reactiepatroon en je huidige routine.',
      },
      {
        title: 'Krijg één passend protocol',
        body: 'De producten die samenwerken, in de juiste volgorde, met een huidplan op papier.',
      },
      {
        title: 'Ik blijf meekijken',
        body: 'Vragen tussendoor via WhatsApp. Na drie weken vraag ik hoe het gaat.',
      },
    ],
    anchorEyebrow: 'Wat ik zelf zou kiezen',
    others: {
      heading: { text: 'Of begin met wat nu het meest stoort' },
      body: 'Drie à vier producten, ongeveer drie maanden. Uit te breiden zodra je huid eraan gewend is.',
    },
    compareHeading: 'In één oogopslag',
    faqTitle: 'Wat als het toch niet past?',
    salonClient: {
      heading: { text: 'Al klant in de salon?' },
      body: 'Zet code {code} in je winkelmand. Dan werk ik je huidplan na je volgende behandeling bij en ligt je bestelling klaar in de salon.',
      cta: 'Vraag {firstName} om advies',
    },
  },

  treatments: {
    hero: {
      eyebrow: 'Gezichtsbehandelingen in {city}',
      heading: { text: 'Huid weer vol leven,', accent: 'van binnenuit.' },
      body: 'Bijna elke behandeling draait om bindweefselmassage: fitness voor je huid. Daaromheen werk ik aan wat je huid die dag nodig heeft, van pigment en roodheid tot verslapping. Resultaat zie je vaak binnen 60 minuten.',
      cta: 'Plan een behandeling',
      secondaryCta: 'Bekijk alle behandelingen',
      agenda: {
        title: 'De agenda zit vaak een paar weken vol.',
        body: 'Start thuis alvast met het protocol dat bij je behandeling hoort.',
        cta: 'Naar de protocollen',
      },
      includesTitle: 'Elke behandeling is inclusief',
      treatedBy: 'Altijd behandeld door',
      // Leeg = de foto "aan het werk" uit Instellingen › Salon & contact (docs/BEELDEN.md, S1).
      media: { image: '', alt: '' },
    },
    includes: [
      'Oppervlaktereiniging',
      'Dieptereiniging met peeling',
      'Massage',
      'Masker',
      'Dagverzorging',
    ],
    bindweefsel: {
      title: 'Bindweefselmassage',
      claim: 'De fitnessoefening voor je huid',
      intro:
        'Kleine kneepjes zetten het weefsel onder druk. Er komt hyaluronzuur vrij, collageen en elastine worden geactiveerd en afvalstoffen worden afgevoerd. Je huid krijgt weer zuurstof.',
      rhythm: 'In kuurvorm om de drie weken een afspraak.',
      forWho: [
        'Je wilt werken aan anti-ageing',
        'Je huid is dof en voelt onregelmatig aan',
        'Je ziet (beginnende) lijntjes',
        'Je bent klaar voor een stralende teint',
      ],
      effects: [
        'Aanmaak van collageen en elastine bevorderen',
        'Zuurstof en hyaluronzuur naar de bovenste huidlaag brengen',
        'Afvalstoffen afvoeren door de doorbloeding te stimuleren',
      ],
      cta: 'Boek een bindweefselmassage',
      secondaryCta: 'Het vitamine C serum erbij',
      media: {
        // Tijdelijk sfeerbeeld, niet de salon zelf: vervangen vóór livegang (docs/BEELDEN.md).
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/e7b2132e-c4c7-4f2e-9292-deb45e0f57e7.jpg',
        alt: 'Twee handen die een gezichtsmassage geven',
        focus: { x: 55, y: 50 },
      },
    },
    bridge: {
      heading: { text: 'Een behandeling is één uur.', accent: 'Je huid leeft er 24/7 mee.' },
      body: 'Wat je in de salon bereikt, houd je thuis vast. Na elke behandeling krijg je persoonlijk advies mee, met de producten die ik op de stoel gebruik.',
      cta: 'Bekijk de thuisprotocollen',
      secondaryCta: 'Alle producten',
    },
    faqTitle: 'Je eerste afspraak',
    closing: {
      heading: { text: 'Twijfel je welke behandeling past?' },
      body: 'Een korte vraag via WhatsApp is gratis. Wil je alles doornemen? Plan {consultName}: {consultMinutes} minuten online voor {consultPrice}.',
      cta: 'Plan {consultName}',
      secondaryCta: 'Stel je vraag via WhatsApp',
    },
  },

  journal: {
    hero: {
      eyebrow: 'Blog & podcast',
      heading: { text: 'Hier deel ik mijn tips', accent: 'en bevindingen.' },
      byline: 'Geschreven en opgenomen door',
    },
    articlesHeading: 'Recent geschreven',
    emptyCategory: 'Binnenkort meer in deze categorie.',
    podcast: {
      eyebrow: 'De podcast',
      heading: { text: 'Gesprekken over huid en bewust leven' },
      body: 'Met gasten die mij inspireren tot een bewuster leven.',
      cta: 'Luister op Spotify',
      topics: ['Energie', 'Lichaamswerk', 'Familieopstellingen', 'Voeding', 'Communicatie'],
      /** Tot de echte podcastlink bekend is: zoeken op Spotify. */
      url: 'https://open.spotify.com/search/Beauty%20Bewust%20podcast',
    },
    episodes: [
      {
        number: 12,
        title: 'Je huid liegt niet',
        guest: 'met orthomoleculair therapeut Ilse de Boer',
        duration: '48 min',
        summary:
          'Over darmen, stress en hardnekkige onzuiverheden. En waar je begint als alles tegelijk speelt.',
      },
      {
        number: 11,
        title: 'Anti-ageing zonder angst',
        guest: 'solo-aflevering',
        duration: '34 min',
        summary:
          'Waarom ik niet geloof in het bestrijden van rimpels, maar wel in een huid die stevig en gezond blijft.',
      },
      {
        number: 10,
        title: 'Plantbased dat écht werkt',
        guest: 'over kokosolie, amandelolie en marketing',
        duration: '41 min',
        summary:
          'Populaire ingrediënten die vaak niets doen, of juist het tegenovergestelde. Waar je wél op let in een ingrediëntenlijst.',
      },
    ],
  },

  about: {
    hero: {
      eyebrow: 'Huidspecialist in Amsterdam',
      heading: { text: 'Hoi, ik ben', accent: '{firstName}.' },
      paragraphs: [
        'Ruim {years} jaar geleden begon ik in een klassieke salon waar iedereen dezelfde behandeling kreeg. Dat wringde. Sindsdien werk ik met mijn eigen behandelingen: bindweefselmassage, ontspanningsmassage en energetisch werk. Een hormonale huid vraagt iets anders dan een huid in de overgang. Beide vragen tijd.',
        'In {city} zie ik één cliënt tegelijk. Ik werk met emerginC, cosmeceuticals die bijna medicinaal zijn, zodat je huid echt verbetert.',
        'Ik beloof geen wonderen. Wel eerlijkheid: over wat haalbaar is, hoe lang het duurt en wanneer je beter naar een arts gaat.',
      ],
      // De eerste alinea staat in de hero, de rest eronder. Knoppen: afspraak en protocollen.
      cta: 'Maak kennis in de salon',
      secondaryCta: 'Bekijk mijn protocollen',
      // Leeg = het portret uit Instellingen › Salon & contact (docs/BEELDEN.md, O1).
      media: { image: '', alt: '' },
    },
    philosophy: {
      eyebrow: 'Waar ik voor sta',
      claim: '70% van je resultaat haal je thuis',
      body: 'Eén behandeling is geen eindpunt. Net als bij sporten: één keer naar de gym maakt je niet fit. Het resultaat komt als je thuis ook serieus aan de slag gaat.',
      // Leeg = de foto "aan het werk" uit Instellingen › Salon & contact.
      media: { image: '', alt: '' },
    },
    shelf: {
      heading: { text: 'Op mijn eigen badkamerplank' },
      body: 'Ik verkoop niets wat ik niet zelf gebruik. Deze staan al jaren naast mijn kraan, en in de salon.',
      cta: 'Bekijk de hele collectie',
    },
    salon: {
      eyebrow: 'De salon',
      heading: { text: '{city}, één behandelkamer.' },
      body: 'Geen wachtkamer vol mensen, geen wisselende specialisten. Kom een paar minuten eerder: er staat thee klaar.',
      contactNote:
        'Vermeld in je mail even je telefoonnummer, dan neem ik binnen {responseHours} uur contact met je op.',
      cta: 'Plan een afspraak',
      secondaryCta: 'Stuur een WhatsApp',
    },
  },

  contact: {
    hero: {
      eyebrow: 'Contact',
      heading: { text: 'Een vraag?', accent: 'App me gewoon.' },
      body: 'Ik lees en beantwoord alles zelf, binnen {responseHours} uur. Ook als je (nog) niets koopt.',
      cta: 'Stuur een WhatsApp',
      secondaryCta: 'Mail me',
    },
    channelsTitle: 'Waarmee kan ik je helpen?',
    channels: {
      skin: {
        title: 'Een vraag over je huid of een product',
        body: 'Stuur er een foto bij daglicht bij, dan kijk ik gericht mee.',
        cta: 'App {firstName}',
      },
      booking: {
        title: 'Een behandeling in de salon',
        body: 'Kies online een dag en tijd die jou past.',
        cta: 'Plan een behandeling',
      },
      advice: {
        title: 'Alles rustig doornemen',
        body: '{consultName}: {consultMinutes} minuten online met {firstName}, voor {consultPrice}.',
        cta: 'Plan {consultName}',
      },
      order: {
        title: 'Je bestelling, levering of retour',
        body: 'Mail je vraag met je bestelnummer erbij. Je bestellingen staan ook in Mijn omgeving.',
        cta: 'Mail over je bestelling',
      },
      late: {
        title: 'Te laat of verhinderd?',
        body: 'App me zo snel mogelijk. Tot {cancelHours} uur vooraf verzet je kosteloos.',
        cta: 'App dat je later bent',
      },
    },
    visit: {
      eyebrow: 'Langskomen',
      heading: { text: 'De salon in {city}' },
      body: 'Alleen op afspraak. Ik behandel één cliënt tegelijk en kan tussendoor niet opendoen.',
      cta: 'Plan een afspraak',
      secondaryCta: 'Eerst iets vragen',
      arriveTitle: 'Zo kom je er',
      mapCta: 'Toon de kaart',
      mapNote: 'De kaart komt van Google Maps. Pas als je hem opent, deelt je browser gegevens met Google.',
    },
  },

  testimonials: [
    {
      quote:
        'Na jaren van zuren en antibiotica is mijn huid eindelijk rustig. Niet door één wonderproduct, maar doordat {firstName} me leerde wat ik zelf thuis moest doen.',
      name: 'Sanne V.',
      context: 'hormonale huid, 34',
    },
    {
      quote:
        'De bindweefselmassage in kuurvorm heeft mijn doffe teint compleet omgedraaid. Ik ga nu elke drie weken.',
      name: 'Marjolein D.',
      context: 'klant sinds 2019',
    },
    {
      quote:
        'De enige plek in Amsterdam waar ik niet het gevoel krijg dat me iets wordt aangesmeerd.',
      name: 'Fleur K.',
      context: 'redacteur & klant',
    },
  ],

  subscription: {
    name: 'Bewust Abonnement',
    claim: 'Je routine loopt door, jij hoeft er niet aan te denken',
    price: 'Levering elke maand of elke twee maanden. Jij kiest het ritme.',
    intro:
      'Op is op is de grootste reden dat mensen halverwege stoppen. Met een abonnement staat je volgende flesje op tijd klaar, en krijg je mijn begeleiding er structureel bij.',
    perks: [
      { title: 'Altijd gratis verzending', body: 'Ongeacht het bedrag van je levering.' },
      {
        title: 'Elke derde levering een cadeau',
        body: 'Een reisformaat of nieuw product uit de collectie, door mij uitgekozen op jouw huid.',
      },
      {
        title: 'Twee huidchecks per jaar',
        body: 'Vijftien minuten videobellen met mij: hoe gaat het, wat passen we aan.',
      },
      {
        title: 'Voorrang in de salon',
        body: 'Abonnees krijgen als eerste bericht bij een vrijgekomen plek.',
      },
    ],
    smallPrint: 'Per levering aanpasbaar, pauzeren of stoppen kan altijd in één klik.',
  },

  portal: {
    tips: {
      retinoidWeek: {
        title: 'Week {week} met {product}',
        body: 'Je huid vernieuwt zich nu. Wat schilfertjes of een strak gevoel is normaal en trekt meestal na week zes weg. Houd je ritme vast, en sla gerust een avond over als het prikt.',
      },
      firstCycle: {
        title: 'Je eerste huidcyclus zit erop',
        body: 'Na {weeks} weken {product} ziet je teint er doorgaans egaler uit. Pigment zelf heeft meer tijd nodig: rond week 12 zie je het echte verschil. Vergelijk het straks in je fotodagboek.',
      },
      firstWeekMany: {
        title: 'Je eerste week',
        body: 'Begin met één nieuw product per paar dagen, niet alles tegelijk. Zo weet je precies waar je huid op reageert.',
      },
      firstWeekOne: {
        title: 'Net begonnen met {product}',
        body: 'De eerste week went je huid aan iets nieuws. Voeg niets anders toe tot je weet hoe ze reageert.',
      },
      patience: {
        title: 'Geduld loont',
        body: 'Je huid vernieuwt zich elke ± 28 dagen. Resultaat zie je per cyclus, niet per dag. Houd je routine vast, ook op drukke dagen.',
      },
      afterTreatment: {
        title: 'Na je {treatment}',
        body: 'Geef je huid vandaag rust: geen scrub, geen actieve stoffen, en smeer morgenochtend SPF. Zo houd je het effect het langst vast.',
      },
      whereToStart: {
        title: 'Waar begin je?',
        body: 'Minder is vaak meer: een milde reiniging, een crème die bij je huid past en elke ochtend SPF. Twijfel je wat jij nodig hebt? Doe de huidtest, of stuur een foto bij daglicht.',
      },
      betweenSessions: {
        title: 'Tussen twee sessies',
        body: 'Een kuur werkt het best {courseSpacing}. Wat je thuis doet telt mee: dagelijks SPF en een milde reiniging houden je huid ontvankelijk voor de volgende behandeling.',
      },
      keepResult: {
        title: 'Het resultaat vasthouden',
        body: 'Een behandeling zet iets in gang; je thuisroutine houdt het vast. Twijfel je wat je thuis zou gebruiken? Vraag het gerust.',
      },
    },
    seasons: {
      cold: {
        title: 'Het wordt kouder',
        body: 'Verwarming en wind drogen je huid uit. Voelt ze strak? Een druppel gezichtsolie in je crème helpt.',
        withProduct:
          'Trekt je huid ’s avonds? Meng je {product} dan met één druppel gezichtsolie. Verwarming en wind drogen meer uit dan je denkt.',
      },
      spring: {
        title: 'De zon wordt sterker',
        body: 'Vanaf nu is SPF elke ochtend echt nodig, ook bij bewolking. Zeker na een behandeling.',
        withProduct: 'Vanaf nu is je {product} elke ochtend echt nodig, ook bij bewolking.',
      },
      summerRetinal: {
        title: 'Zomer en retinal',
        body: 'Gebruik je {product} in de zomer alleen ’s avonds en smeer overdag elke twee uur SPF bij als je buiten bent.',
      },
    },
    stopReasons: {
      genoeg: 'Ik heb nog genoeg',
      'te-duur': 'Het is te duur',
      huid: 'Mijn huid reageert niet goed',
      'iets-anders': 'Ik probeer iets anders',
      anders: 'Andere reden',
    },
  },
}

/**
 * De standaardteksten zoals ze in de code staan, vóór iets uit de backend ze overschrijft.
 * Hiermee vult `upgradePageContent` een oudere opslag aan met nieuwe velden (bijv. een nieuw beeld),
 * zodat die in het beheer en op de site meteen bestaan. Nooit wijzigen.
 */
export const pageDefaults: PageContent = structuredClone(pages)
